import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ResetPasswordFormFieldView from "./reset-password-form-field-view";
import ResetPasswordSubmitView from "./reset-password-submit-view";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import { createResetPasswordSchema } from "@/schema";
import useResetPassword from "@/hooks/use-reset-password";
import { Box, Typography } from "@mui/material";
import useForgotPassword from "@/hooks/use-forgot-password";

const ResetPasswordForm = ({
  token,
  email,
}: {
  token: string;
  email: string;
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const ResetPasswordSchema = createResetPasswordSchema(
    t as (key: string) => string
  );
  const navigate = useNavigate();
  const { resetPassword, isLoading, error } = useResetPassword();

  const form = useForm<z.infer<typeof ResetPasswordSchema>>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      email,
      newPassword: "",
      code: token,
    },
  });

  const onSubmit = async (values: z.infer<typeof ResetPasswordSchema>) => {
    try {
      await resetPassword(values);
      navigate(routes.login);
    } catch (err) {}
  };
  const { sendForgotPasswordEmail, isLoading: loadingForg } =
    useForgotPassword();

  const handleResendCode = async () => {
    try {
      await sendForgotPasswordEmail({ email });
    } catch (err) {}
  };
  return (
    <Box
      component="form"
      onSubmit={form.handleSubmit(onSubmit)}
      autoComplete="off"
      sx={{ width: "100%" }}
    >
      <ResetPasswordFormFieldView
        name="code"
        isLoading={loadingForg}
        handleResendCode={handleResendCode}
        label={t("authentication.fields.token")}
        placeholder={t("authentication.placeholders.enterToken")}
        inputType="text"
        formControl={form.control}
      />
      <ResetPasswordFormFieldView
        name="newPassword"
        isLoading={loadingForg}
        handleResendCode={handleResendCode}
        label={t("authentication.changePassword.fields.newPassword")}
        placeholder={t(
          "authentication.changePassword.placeholders.enterNewPassword"
        )}
        inputType="password"
        formControl={form.control}
      />
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1, mb: 1 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ mt: 2 }}>
        <ResetPasswordSubmitView isLoading={isLoading} />
      </Box>
    </Box>
  );
};

export default ResetPasswordForm;
